import React, { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Settings, CreditCard, LogOut, User, Menu, Edit3, X } from 'lucide-react';
import { TokenManager, cancelTrialSubscription, processPlanUpgradePayment, getBillingHistory } from '../api';
import { cancelSubscription, deleteSubscription } from '../api-v2';
import * as Sentry from '@sentry/react';

interface DashboardProps {
  isDarkMode?: boolean;
  toggleDarkMode?: () => void;
}

// Add this button component to test Sentry's error tracking
function ErrorButton() {
  return (
    <button
      onClick={() => {
        throw new Error('This is your first error!');
      }}
      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all duration-300 font-medium transform hover:scale-105 flex items-center gap-2"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      Test Sentry Error
    </button>
  );
}

const Dashboard: React.FC<DashboardProps> = () => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [activeSection, setActiveSection] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [hiddenFields, setHiddenFields] = useState<{[key: string]: boolean}>({
    cardholderName: true,
    fullCardNumber: true
  });
  
  // Card update modal state
  const [showUpdateCardModal, setShowUpdateCardModal] = useState(false);
  const [updateCardData, setUpdateCardData] = useState({
    cardholderName: '',
    expiryMonth: '',
    expiryYear: ''
  });
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Helper function to detect card brand from card number
  const detectCardBrand = (cardNumber: string): string => {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (/^4/.test(cleanNumber)) return 'visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'amex';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'discover';
    if (/^(?:2131|1800|35\d{3})/.test(cleanNumber)) return 'jcb';
    
    return 'unknown';
  };
  
  // Helper function to calculate correct amount based on plan ID
  const calculateCorrectAmount = (planId: string): number => {
    const PLAN_PRICES: { [key: string]: number } = {
      // Monthly pricing
      starter_monthly: 29,   
      standard_monthly: 49,   
      pro_monthly: 99,      
      
      // Yearly pricing (full yearly amounts)
      starter_yearly: 12 * 23,   // 12 * $23 = $276
      standard_yearly: 12 * 39,  // 12 * $39 = $468
      pro_yearly: 12 * 79        // 12 * $79 = $948
    };

    const planPrice = PLAN_PRICES[planId];
    
    if (!planPrice) {
      console.error(`❌ Unknown planId: ${planId}, defaulting to standard_monthly`);
      return 49; 
    }

    console.log(`Correct renewal amount for ${planId}: $${planPrice}`);
    return planPrice;
  };
  
  // Subscription data state
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [planData, setPlanData] = useState<any>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  
  // Plan upgrade modal state
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const [subscriptionUpdateError, setSubscriptionUpdateError] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly'); // Default to yearly
  
  // Trial cancellation state
  const [isCancellingTrial, setIsCancellingTrial] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  
  // Inline name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingFullName, setEditingFullName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Cancel subscription modal state
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [deleteType, setDeleteType] = useState<'cancel' | 'delete'>('cancel'); // Track which action user wants

  // Simple password update modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Billing history state
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [isLoadingBillingHistory, setIsLoadingBillingHistory] = useState(false);
  const [showBillingHistory, setShowBillingHistory] = useState(false);

  // Helper functions for date formatting
  const parseDate = (date: any): Date | null => {
    if (!date) return null;
    
    let dateObj: Date;
    
    // Handle Firestore Timestamp objects (have seconds and nanoseconds properties)
    if (date && typeof date === 'object' && date.seconds) {
      dateObj = new Date(date.seconds * 1000);
    }
    // Handle Firestore Timestamp objects that might have _seconds property
    else if (date && typeof date === 'object' && date._seconds) {
      dateObj = new Date(date._seconds * 1000);
    }
    // Handle Date objects or date strings
    else if (date instanceof Date) {
      dateObj = date;
    }
    else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    }
    // Handle other Firestore timestamp formats
    else if (date && typeof date === 'object' && date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    }
    // Fallback for unexpected formats
    else {
      console.warn('Unknown date format:', date);
      return null;
    }
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date object created from:', date);
      return null;
    }
    
    return dateObj;
  };

  const formatDate = (date: any) => {
    const dateObj = parseDate(date);
    if (!dateObj) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatShortDate = (date: any) => {
    const dateObj = parseDate(date);
    if (!dateObj) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to get the correct display amount for subscription
  const getDisplayAmount = (amount: number, billingCycle: string, planId: string) => {
    if (billingCycle === 'yearly') {
      // For yearly plans, if the amount seems like a monthly amount (< 100), multiply by 12
      // This handles cases where subscription stores monthly equivalent but should display yearly total
      if (amount < 100) {
        return amount * 12;
      }
      // Otherwise assume it's already the yearly total
      return amount;
    }
    // For monthly plans, use amount as-is
    return amount;
  };

  // Get actual plan data from database
  const getPlanFromDB = (planType: string, cycle: 'monthly' | 'yearly') => {
    const planId = cycle === 'yearly' ? `${planType.toLowerCase()}_yearly` : `${planType.toLowerCase()}_monthly`;
    const plan = availablePlans.find(p => (p.planId || p.id) === planId);
    
    console.log(`🔍 [Dashboard] Looking for plan: ${planId}`);
    console.log(`📋 [Dashboard] Available plans:`, availablePlans.map(p => ({ id: p.planId || p.id, sms_limit: p.sms_limit })));
    console.log(`📊 [Dashboard] Found plan:`, plan);
    
    if (plan) {
      const messageLimit = plan.sms_limit || getMessageLimitFromFeatures(plan.features);
      console.log(`💬 [Dashboard] Message limit for ${planId}: ${messageLimit} (sms_limit: ${plan.sms_limit})`);
      
      return {
        price: plan.price,
        features: plan.features || [],
        description: plan.description,
        messageLimit: messageLimit // Use sms_limit from DB first
      };
    }
    
    // Fallback if plan not found in DB
    console.log(`⚠️ [Dashboard] Plan ${planId} not found, using fallback`);
    return {
      price: planType === 'Starter' ? 29 : planType === 'Standard' ? 49 : 99,
      features: getDefaultFeatures(planType),
      description: getDefaultDescription(planType),
      messageLimit: planType === 'Starter' ? 750 : planType === 'Standard' ? 1750 : 4000
    };
  };

  // Extract message limit from features array
  const getMessageLimitFromFeatures = (features: string[] = []) => {
    const messageFeature = features.find(f => f.includes('messages') || f.includes('_messages_'));
    if (messageFeature) {
      const numbers = messageFeature.match(/\d+/g);
      if (numbers && numbers.length > 0) {
        return parseInt(numbers[0]);
      }
    }
    return 750; // default fallback
  };

  // Default features fallback
  const getDefaultFeatures = (planType: string) => {
    if (planType === 'Starter') {
      return ['Unlimited AI conversations', 'Basic mood tracking', 'Mobile app access'];
    } else if (planType === 'Standard') {
      return ['Everything in Starter', 'Advanced analytics & insights', 'Goal setting & tracking'];
    } else {
      return ['Everything in Standard', 'Direct therapist consultations', 'Personalized treatment plans'];
    }
  };

  // Default description fallback
  const getDefaultDescription = (planType: string) => {
    if (planType === 'Starter') return 'Perfect for getting started';
    if (planType === 'Standard') return 'Most comprehensive support';
    return 'For serious mental health focus';
  };

  useEffect(() => {
    // Fetch user profile data from Firebase using email
    const fetchUserProfileByEmail = async () => {
      if (!user?.emailAddresses?.[0]?.emailAddress) {
        console.log('❌ No email address available for profile lookup');
        return;
      }

      setIsLoadingProfile(true);
      try {
        const userEmail = user.emailAddresses[0].emailAddress;
        console.log('🔍 Fetching profile for email:', userEmail);
        
        // Use the same API configuration as api-v2.ts
        const API_BASE = (() => {
          const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
          const localUrl = 'http://localhost:5001';
          
          // Check if we're forcing production API
          if (process.env.REACT_APP_USE_PROD_API === 'true') {
            console.log('🌐 Using production API endpoint (forced):', prodUrl);
            return prodUrl;
          }
          
          // Check if we're in production build
          if (process.env.NODE_ENV === 'production') {
            console.log('🌐 Using production API endpoint:', prodUrl);
            return prodUrl;
          }
          
          // Default to local for development
          console.log('🔧 Using local API endpoint:', localUrl);
          return localUrl;
        })();
          
        const apiUrl = `${API_BASE}/user/user-profile?email=${encodeURIComponent(userEmail)}`;
        console.log('🔗 API URL:', apiUrl);
        
        const response = await fetch(apiUrl);
        console.log('📡 Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const profileResponse = await response.json();
        console.log('📦 Profile response:', profileResponse);
        
        if (profileResponse.success && profileResponse.users && profileResponse.users.length > 0) {
          console.log('📋 Found multiple users:', profileResponse.users.length);
          console.log('👥 All users:', profileResponse.users);
          
          // Find the user with the most complete data (non-default values)
          let selectedUser = profileResponse.users[0]; // Default to first user
          
          // Look for a user that doesn't have default/placeholder values
          for (const user of profileResponse.users) {
            console.log(`🔍 Checking user ${user.userId}:`, {
              fullName: user.fullName,
              hasClerkId: !!user.clerkUserId
            });
            
            // Prefer users that don't have "Unknown User" as default values
            if (user.fullName !== 'Unknown User') {
              selectedUser = user;
              console.log('✅ Selected user with real data:', user);
              break;
            }
            
            // Also prefer users with clerkUserId if available
            if (user.clerkUserId && !selectedUser.clerkUserId) {
              selectedUser = user;
            }
          }
          
          setUserProfileData(selectedUser);
          console.log('✅ User profile data loaded:', selectedUser);
          console.log('👤 Username found:', selectedUser.username);
        } else {
          console.log('❌ No profile data found for email:', userEmail);
          console.log('📋 Response structure:', Object.keys(profileResponse));
        }
      } catch (error) {
        console.error('❌ Error fetching user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    if (isLoaded && user) {
      console.log('🔄 User is loaded, fetching profile data by email...');
      fetchUserProfileByEmail();
    }
  }, [isLoaded, user]);

  // Fetch subscription data using email (most reliable approach)
  const fetchSubscriptionByEmail = async (userEmail: string) => {
    setIsLoadingSubscription(true);
    try {
      console.log('🔍 Fetching subscription for email:', userEmail);
      
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();
      
      // Use email-based approach directly (most reliable)
      console.log('🔄 Fetching subscription by email...');
      const emailApiUrl = `${API_BASE}/subscriptions/by-email?email=${encodeURIComponent(userEmail)}`;
      console.log('🔗 Email subscription API URL:', emailApiUrl);
      
      const emailResponse = await fetch(emailApiUrl);
      console.log('📡 Email subscription response status:', emailResponse.status, emailResponse.statusText);
      
      if (!emailResponse.ok) {
        const errorText = await emailResponse.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`HTTP ${emailResponse.status}: ${emailResponse.statusText}`);
      }
      
      const emailResult = await emailResponse.json();
      console.log('📦 Email subscription response:', emailResult);
      
      if (emailResult.success && emailResult.subscription) {
        setSubscriptionData(emailResult.subscription);
        console.log('✅ Subscription data loaded by email:', emailResult.subscription);
        console.log('📅 DEBUG - Start date structure:', emailResult.subscription.startDate);
        console.log('📅 DEBUG - End date structure:', emailResult.subscription.endDate);
        
        // Set plan data if it's included in the response
        if (emailResult.plan) {
          setPlanData(emailResult.plan);
          console.log('✅ Plan data loaded from email response:', emailResult.plan);
        } else {
          console.log('⚠️ No plan data included in email response');
          setPlanData(null);
        }
      } else {
        console.log('❌ No subscription found for email:', userEmail);
        setSubscriptionData(null);
        setPlanData(null);
      }
      
    } catch (error: any) {
      console.error('❌ Error fetching subscription:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error details:', error);
      
      // Set to null to show "no subscription" state instead of loading indefinitely
      setSubscriptionData(null);
      setPlanData(null);
      
      // Optional: Show user-friendly error message
      if (error.message.includes('500')) {
        console.error('❌ Server error - this might be a temporary issue');
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Fetch available plans
  const fetchAvailablePlans = async () => {
    setIsLoadingPlans(true);
    try {
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();
        
      const response = await fetch(`${API_BASE}/plans`);
      console.log('📦 Plans response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📋 Available plans full response:', result);
      
      if (result.success && result.plans) {
        setAvailablePlans(result.plans);
        console.log('✅ Plans loaded:', result.plans.length);
        console.log('📋 Available plans detailed:', result.plans.map((p: any) => ({ 
          id: p.id || p.planId, 
          name: p.name, 
          price: p.price, 
          sms_limit: p.sms_limit,
          planId: p.planId 
        })));
        console.log('🎯 Current subscription plan ID:', subscriptionData?.planId);
      } else {
        console.log('❌ No plans found');
        setAvailablePlans([]);
      }
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      setAvailablePlans([]);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  // Fetch billing history for the user
  const fetchBillingHistory = async (userEmail: string) => {
    setIsLoadingBillingHistory(true);
    try {
      console.log('📊 Fetching billing history for:', userEmail);
      
      // Get API base URL
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();
      
      console.log('🌐 Using API endpoint:', API_BASE);
      
      // Direct fetch to billing endpoint
      const response = await fetch(`${API_BASE}/billing/history/${encodeURIComponent(userEmail)}`);
      console.log('📡 Billing API Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📊 Billing API Response data:', result);
      
      if (result.success) {
        // Ensure billing history is sorted by date (latest first)
        const sortedHistory = (result.billingHistory || []).sort((a: any, b: any) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Latest first
        });
        
        setBillingHistory(sortedHistory);
        console.log('✅ Billing history loaded and sorted:', {
          count: sortedHistory.length,
          firstRecord: sortedHistory[0],
          lastRecord: sortedHistory[sortedHistory.length - 1]
        });
      } else {
        console.error('❌ Failed to fetch billing history:', result.message);
        setBillingHistory([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching billing history:', error);
      
      // Try alternative approach - check if billing collection exists
      console.log('🔄 Trying alternative approach...');
      try {
        const API_BASE = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const checkResponse = await fetch(`${API_BASE}/check-billing`);
        const checkResult = await checkResponse.json();
        console.log('🔍 Billing collection check:', checkResult);
      } catch (checkError) {
        console.error('❌ Alternative approach also failed:', checkError);
      }
      
      setBillingHistory([]);
    } finally {
      setIsLoadingBillingHistory(false);
    }
  };

  // Handle opening upgrade plan modal
  const handleOpenUpgradePlanModal = () => {
    const currentPlanId = subscriptionData?.planId || '';
    console.log('🔄 Opening upgrade modal, current plan ID:', currentPlanId);
    console.log('📋 Current subscription data:', subscriptionData);
    setSelectedPlanId(currentPlanId);
    setSubscriptionUpdateError('');
    setShowUpgradePlanModal(true);
    fetchAvailablePlans();
  };

  // Handle closing upgrade plan modal
  const handleCloseUpgradePlanModal = () => {
    setShowUpgradePlanModal(false);
    setSelectedPlanId('');
    setSubscriptionUpdateError('');
  };

  // Handle updating subscription plan
  const handleUpdateSubscriptionPlan = async () => {
    if (!selectedPlanId || selectedPlanId === subscriptionData?.planId) {
      setSubscriptionUpdateError('Please select a different plan');
      return;
    }

    if (!user?.emailAddresses?.[0]?.emailAddress) {
      setSubscriptionUpdateError('User email not found');
      return;
    }

    setIsUpdatingSubscription(true);
    setSubscriptionUpdateError('');

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      const selectedPlan = availablePlans.find(plan => plan.id === selectedPlanId);
      
      if (!selectedPlan) {
        throw new Error('Selected plan not found');
      }

      // Get API base URL
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      // First get the user's payment methods
      const userProfileResponse = await fetch(`${API_BASE}/user/user-profile?email=${encodeURIComponent(userEmail)}`);
      const userProfileResult = await userProfileResponse.json();
      
      if (!userProfileResult.success || !userProfileResult.users || userProfileResult.users.length === 0) {
        throw new Error('User profile not found');
      }

      // Find the user with the most complete data
      let selectedUser = userProfileResult.users[0];
      for (const userData of userProfileResult.users) {
        if (userData.fullName !== 'Unknown User') {
          selectedUser = userData;
          break;
        }
      }

      // Get primary card info
      let primaryCard;
      if (selectedUser?.cards && Array.isArray(selectedUser.cards)) {
        primaryCard = selectedUser.cards.find((card: { cardName: string }) => card.cardName === "primaryCard");
      } else if (selectedUser?.paymentMethods) {
        const primaryMethodKey = Object.keys(selectedUser.paymentMethods).find(key => 
          selectedUser.paymentMethods[key].isDefault
        );
        if (primaryMethodKey) {
          primaryCard = selectedUser.paymentMethods[primaryMethodKey];
        }
      }

      if (!primaryCard || !primaryCard.stripeCustomerId || !primaryCard.stripePaymentMethodId) {
        throw new Error('Primary payment method not found. Please add a payment method first.');
      }

      console.log('💳 Processing payment for plan upgrade:', {
        planId: selectedPlanId,
        customerId: primaryCard.stripeCustomerId,
        paymentMethodId: primaryCard.stripePaymentMethodId
      });

      // Calculate the correct amount based on billing cycle
      let paymentAmount = selectedPlan.price;
      if (selectedPlanId.includes('_yearly')) {
        // For yearly plans, charge the full yearly amount
        paymentAmount = selectedPlan.price * 12;
        console.log(`💰 Yearly plan detected: ${selectedPlan.price}/month × 12 = $${paymentAmount}`);
        console.log(`📊 Plan details: ${selectedPlanId} - Monthly: $${selectedPlan.price}, Yearly: $${paymentAmount}`);
      } else {
        console.log(`💰 Monthly plan: $${paymentAmount}`);
        console.log(`📊 Plan details: ${selectedPlanId} - Monthly: $${paymentAmount}`);
      }

      // Validate that the amount makes sense
      if (paymentAmount <= 0 || paymentAmount > 10000) {
        throw new Error(`Invalid payment amount: $${paymentAmount}. Please contact support.`);
      }

      // First process the payment
      const paymentResult = await processPlanUpgradePayment({
        email: userEmail,
        planId: selectedPlanId,
        amount: paymentAmount,
        stripeCustomerId: primaryCard.stripeCustomerId,
        stripePaymentMethodId: primaryCard.stripePaymentMethodId,
        subscriptionId: subscriptionData.subscriptionId
      });

      if (!paymentResult.success) {
        throw new Error(paymentResult.message || 'Payment failed');
      }

      console.log('✅ Payment processed successfully:', paymentResult);

      // Now update the subscription
      const updateData = {
        email: userEmail,
        planId: selectedPlanId,
        amount: paymentAmount, // Use the calculated payment amount
        stripePaymentIntentId: paymentResult.paymentIntentId
      };

      console.log('📤 Sending subscription update request:', updateData);

      const response = await fetch(`${API_BASE}/subscriptions/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      console.log('📡 Update response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Update result:', result);

      if (result.success) {
        console.log('✅ Subscription updated successfully');
        
        // Close modal first
        setShowUpgradePlanModal(false);
        setSelectedPlanId('');
        
        // Fetch fresh subscription data from backend to get all updated fields
        console.log('🔄 Refreshing subscription data after update...');
        await fetchSubscriptionByEmail(userEmail);
        
        console.log('✅ Subscription data refreshed');
      } else {
        setSubscriptionUpdateError(result.message || 'Failed to update subscription');
      }
    } catch (error: any) {
      console.error('❌ Error updating subscription:', error);
      setSubscriptionUpdateError(error.message || 'Failed to update subscription. Please try again.');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  // Add useEffect to fetch subscription data when user is loaded
  useEffect(() => {
    if (isLoaded && user?.emailAddresses?.[0]?.emailAddress) {
      const userEmail = user.emailAddresses[0].emailAddress;
      fetchSubscriptionByEmail(userEmail);
      fetchBillingHistory(userEmail);
    }
  }, [isLoaded, user, userProfileData]);

  // Fetch plans when subscription data is loaded and there's no subscription
  useEffect(() => {
    if (!isLoadingSubscription && !subscriptionData && isLoaded) {
      fetchAvailablePlans();
    }
  }, [isLoadingSubscription, subscriptionData, isLoaded]);



  // Handle plan selection for new subscription
  const handlePlanSelection = (planId: string) => {
    console.log('🎯 Plan selected:', planId);
    
    // Verify the plan exists in available plans
    const selectedPlan = availablePlans.find(plan => 
      plan.id === planId || plan.planId === planId
    );
    
    if (!selectedPlan) {
      console.error('❌ Selected plan not found in available plans:', {
        planId,
        availablePlans: availablePlans.map(p => ({ id: p.id || p.planId, planId: p.planId }))
      });
      setSubscriptionUpdateError('Invalid plan selection. Please try again.');
      return;
    }

    console.log('✅ Found selected plan:', selectedPlan);
    setSelectedPlanId(planId);
    setSubscriptionUpdateError('');
  };

  // Handle manual subscription renewal
  const handleRenewSubscription = async () => {
    if (!user?.emailAddresses?.[0]?.emailAddress) {
      setSubscriptionUpdateError('User email not found');
      return;
    }

    if (!subscriptionData?.planId) {
      setSubscriptionUpdateError('No active subscription found');
      return;
    }

    setIsUpdatingSubscription(true);
    setSubscriptionUpdateError('');

    try {
      const userEmail = user.emailAddresses[0].emailAddress;

      // Get API base URL
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      // First get the user's payment methods
      const userProfileResponse = await fetch(`${API_BASE}/user/user-profile?email=${encodeURIComponent(userEmail)}`);
      const userProfileResult = await userProfileResponse.json();
      
      if (!userProfileResult.success || !userProfileResult.users || userProfileResult.users.length === 0) {
        throw new Error('User profile not found');
      }

      // Find the user with the most complete data
      let selectedUser = userProfileResult.users[0];
      for (const userData of userProfileResult.users) {
        if (userData.fullName !== 'Unknown User') {
          selectedUser = userData;
          break;
        }
      }

      // Get primary card info
      let primaryCard;
      if (selectedUser?.cards && Array.isArray(selectedUser.cards)) {
        primaryCard = selectedUser.cards.find((card: { cardName: string }) => card.cardName === "primaryCard");
      } else if (selectedUser?.paymentMethods) {
        const primaryMethodKey = Object.keys(selectedUser.paymentMethods).find(key => 
          selectedUser.paymentMethods[key].isDefault
        );
        if (primaryMethodKey) {
          primaryCard = selectedUser.paymentMethods[primaryMethodKey];
        }
      }

      if (!primaryCard || !primaryCard.stripeCustomerId || !primaryCard.stripePaymentMethodId) {
        throw new Error('Primary payment method not found. Please add a payment method first.');
      }

      console.log('💳 Processing payment for subscription renewal:', {
        planId: subscriptionData.planId,
        customerId: primaryCard.stripeCustomerId,
        paymentMethodId: primaryCard.stripePaymentMethodId
      });

      // Calculate the correct renewal amount
      const correctRenewalAmount = calculateCorrectAmount(subscriptionData.planId);

      // First process the payment
      const paymentResult = await processPlanUpgradePayment({
        email: userEmail,
        planId: subscriptionData.planId,
        amount: correctRenewalAmount, // Use calculated correct amount
        stripeCustomerId: primaryCard.stripeCustomerId,
        stripePaymentMethodId: primaryCard.stripePaymentMethodId,
        subscriptionId: subscriptionData.subscriptionId
      });

      if (!paymentResult.success) {
        throw new Error(paymentResult.message || 'Payment failed');
      }

      console.log('✅ Payment processed successfully:', paymentResult);

      // Now update the subscription
      const updateData = {
        email: userEmail,
        planId: subscriptionData.planId,
        amount: correctRenewalAmount, // Use calculated correct amount
        stripePaymentIntentId: paymentResult.paymentIntentId,
        isRenewal: true
      };

      console.log('📤 Sending subscription renewal request:', updateData);

      const response = await fetch(`${API_BASE}/subscriptions/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      console.log('📡 Update response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Update result:', result);

      if (result.success) {
        console.log('✅ Subscription renewed successfully');
        
        // Fetch fresh subscription data from backend to get all updated fields
        console.log('🔄 Refreshing subscription data after renewal...');
        await fetchSubscriptionByEmail(userEmail);
        
        console.log('✅ Subscription data refreshed');
      } else {
        setSubscriptionUpdateError(result.message || 'Failed to renew subscription');
      }
    } catch (error: any) {
      console.error('❌ Error renewing subscription:', error);
      setSubscriptionUpdateError(error.message || 'Failed to renew subscription. Please try again.');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  // Handle creating a new subscription
  const handleCreateSubscription = async () => {
    console.log('🔄 Starting subscription creation');
    console.log('📋 Selected plan ID:', selectedPlanId);
    console.log('📋 Available plans:', availablePlans);

    if (!selectedPlanId) {
      console.error('❌ No plan selected');
      setSubscriptionUpdateError('Please select a plan');
      return;
    }

    // Verify the selected plan exists in available plans
    const selectedPlan = availablePlans.find(plan => 
      plan.id === selectedPlanId || plan.planId === selectedPlanId
    );
    
    if (!selectedPlan) {
      console.error('❌ Selected plan not found in available plans:', {
        selectedPlanId,
        availablePlans: availablePlans.map(p => ({ id: p.id || p.planId, planId: p.planId }))
      });
      setSubscriptionUpdateError('Selected plan not found. Please try selecting a plan again.');
      return;
    }

    console.log('✅ Found selected plan:', selectedPlan);

    if (!user?.emailAddresses?.[0]?.emailAddress) {
      console.error('❌ No user email found');
      setSubscriptionUpdateError('User email not found');
      return;
    }

    // Check if we have phone number from profile, if not fetch it from user collection
    let phoneNumber = userProfileData?.phoneNumber;
    if (!phoneNumber) {
      console.log('📱 Phone number not found in profile, fetching from user collection...');
      try {
        const userEmail = user.emailAddresses[0].emailAddress;
        const API_BASE = (() => {
          const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
          const localUrl = 'http://localhost:5001';
          
          if (process.env.REACT_APP_USE_PROD_API === 'true') {
            return prodUrl;
          }
          
          if (process.env.NODE_ENV === 'production') {
            return prodUrl;
          }
          
          return localUrl;
        })();

        const userResponse = await fetch(`${API_BASE}/users/by-email?email=${encodeURIComponent(userEmail)}`);
        if (userResponse.ok) {
          const userResult = await userResponse.json();
          if (userResult.success && userResult.users && userResult.users.length > 0) {
            phoneNumber = userResult.users[0].phoneNumber;
            console.log('📱 Found phone number from user collection:', phoneNumber);
          }
        }
      } catch (fetchError) {
        console.error('❌ Error fetching user phone number:', fetchError);
      }
    }

    if (!phoneNumber) {
      setSubscriptionUpdateError('User phone number not found. Please ensure your profile is complete.');
      return;
    }

    setIsUpdatingSubscription(true);
    setSubscriptionUpdateError('');

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      const selectedPlan = availablePlans.find(plan => plan.id === selectedPlanId);
      
      if (!selectedPlan) {
        throw new Error('Selected plan not found');
      }

      console.log('🔄 Creating new subscription for plan:', selectedPlanId);
      console.log('👤 User profile data:', userProfileData);

      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      // Get stripeCustomerId from multiple sources
      let stripeCustomerId = null;
      
      // First try to get it from user profile data (top-level)
      if (userProfileData?.stripeCustomerId) {
        stripeCustomerId = userProfileData.stripeCustomerId;
        console.log('💳 Using stripeCustomerId from user profile:', stripeCustomerId);
      }
      // If not found, try to get it from payment methods
      else if (paymentMethods && paymentMethods.length > 0 && paymentMethods[0].stripeCustomerId) {
        stripeCustomerId = paymentMethods[0].stripeCustomerId;
        console.log('💳 Using stripeCustomerId from payment methods:', stripeCustomerId);
      }

      if (!stripeCustomerId) {
        setSubscriptionUpdateError('Stripe customer ID not found. Please add a payment method first or ensure your account is properly set up.');
        return;
      }

      // Calculate the correct amount for subscription creation
      const correctCreationAmount = calculateCorrectAmount(selectedPlanId);

      const subscriptionData = {
        email: userEmail,
        phoneNumber: phoneNumber, // Use the fetched phone number
        fullName: userProfileData?.fullName || 'Unknown User',
        planId: selectedPlanId,
        amount: correctCreationAmount, // Use calculated correct amount, not database price
        stripeCustomerId: stripeCustomerId
      };
      
      console.log('💰 Subscription creation pricing:', {
        planId: selectedPlanId,
        databasePrice: selectedPlan.price,
        calculatedAmount: correctCreationAmount,
        usingCalculated: true
      });

      console.log('📤 Creating subscription with data:', subscriptionData);

      const response = await fetch(`${API_BASE}/user/paid-user-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData)
      });

      console.log('📡 Create subscription response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Create subscription result:', result);

      if (result.success) {
        // Update local state with new subscription data
        // Auto-detect billing cycle from plan ID
        const detectedBillingCycle = selectedPlanId.includes('_yearly') ? 'yearly' : 'monthly';
        setSubscriptionData({
          planId: selectedPlanId,
          amount: selectedPlan.price,
          billingCycle: detectedBillingCycle,
          currency: 'usd',
          status: 'active',
          subscriptionId: result.id,
          userId: userProfileData.userId,
          email: userEmail,
          phoneNumber: userProfileData.phoneNumber
        });
        setPlanData(selectedPlan);
        
        // Reset selection state
        setSelectedPlanId('');
        
        console.log('✅ Subscription created successfully');
        
        // Refresh subscription data to get the latest from server with all fields
        console.log('🔄 Refreshing subscription data after creation...');
        await fetchSubscriptionByEmail(userEmail);
        
        console.log('✅ Subscription data refreshed after creation');
      } else {
        setSubscriptionUpdateError(result.message || 'Failed to create subscription');
      }
    } catch (error: any) {
      console.error('❌ Error creating subscription:', error);
      setSubscriptionUpdateError(error.message || 'Failed to create subscription. Please try again.');
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  // Handle trial cancellation
  const handleCancelTrial = async () => {
    if (!user?.emailAddresses?.[0]?.emailAddress || !subscriptionData?.subscriptionId) {
      setSubscriptionUpdateError('Unable to cancel trial. Missing required information.');
      return;
    }

    setIsCancellingTrial(true);
    setSubscriptionUpdateError('');

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      
      await cancelTrialSubscription({
        email: userEmail,
        subscriptionId: subscriptionData.subscriptionId,
        reason: cancelReason || 'User requested cancellation'
      });

      console.log('✅ Trial cancelled successfully');
      
      // Refresh subscription data
      await fetchSubscriptionByEmail(userEmail);
      
      // Close modal and reset state
      setShowCancelModal(false);
      setCancelReason('');
      
      alert('Your trial has been cancelled successfully. You will not be charged.');
      
    } catch (error: any) {
      console.error('❌ Error cancelling trial:', error);
      setSubscriptionUpdateError(error.message || 'Failed to cancel trial. Please try again.');
    } finally {
      setIsCancellingTrial(false);
    }
  };

  // Handle starting inline edit
  const handleStartEditName = () => {
    setEditingFullName(userData.fullName);
    setIsEditingName(true);
    setUpdateError('');
  };

  // Handle canceling inline edit
  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingFullName('');
    setUpdateError('');
  };

  // Handle saving inline edit
  const handleSaveFullName = async () => {
    if (!editingFullName.trim()) {
      setUpdateError('Full name cannot be empty');
      return;
    }

    if (editingFullName.trim() === userData.fullName) {
      setUpdateError('New name is the same as current name');
      return;
    }

    setIsUpdating(true);
    setUpdateError('');

    try {
      // Use the same API configuration as other calls
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      const updateData = {
        email: userData.email,
        fullName: editingFullName.trim()
      };

      console.log('🔄 Updating user data:', updateData);

      const response = await fetch(`${API_BASE}/user/user-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      console.log('📡 Update response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Update result:', result);

      if (result.success) {
        // Update local state
        setUserProfileData((prev: any) => ({
          ...prev,
          fullName: editingFullName.trim()
        }));

        // Exit edit mode
        setIsEditingName(false);
        setEditingFullName('');
        
        console.log('✅ Full name updated successfully');
      } else {
        setUpdateError(result.message || 'Failed to update name');
      }
    } catch (error: any) {
      console.error('❌ Error updating full name:', error);
      setUpdateError(error.message || 'Failed to update name. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Simple password update handlers
  const handleOpenPasswordModal = () => {
    setShowPasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordUpdate = async () => {
    // Validation
    if (!currentPassword.trim()) {
      setPasswordError('Please enter your current password');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    setIsUpdatingPassword(true);
    setPasswordError('');

    try {
      if (!user) {
        throw new Error('User not found');
      }

      await user.updatePassword({
        currentPassword: currentPassword,
        newPassword: newPassword,
        signOutOfOtherSessions: true
      });

      setPasswordSuccess('Password updated successfully! You have been signed out of other sessions.');
      setTimeout(() => {
        handleClosePasswordModal();
      }, 2000);

    } catch (error: any) {
      console.error('Password update error:', error);
      if (error.errors && error.errors[0]) {
        const clerkError = error.errors[0];
        switch (clerkError.code) {
          case 'form_password_incorrect':
            setPasswordError('Current password is incorrect');
            break;
          case 'form_password_pwned':
            setPasswordError('This password has been found in a data breach. Please choose a different password.');
            break;
          default:
            setPasswordError(clerkError.message || 'Failed to update password');
        }
      } else {
        setPasswordError('Failed to update password. Please try again.');
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Handle cancel/delete subscription
  const handleCancelSubscription = async () => {
    if (!subscriptionData?.subscriptionId) {
      console.error('❌ No subscription ID found');
      setSubscriptionUpdateError('No subscription ID found');
      return;
    }

    setIsCancelling(true);
    setSubscriptionUpdateError('');
    
    try {
      let result;
      
      if (deleteType === 'delete') {
        console.log('🔄 Permanently deleting subscription:', subscriptionData.subscriptionId);
        result = await deleteSubscription(subscriptionData.subscriptionId);
        console.log('✅ Subscription deleted successfully:', result);
        
        // Clear subscription data completely to show plan selection UI
        setSubscriptionData(null);
        setPlanData(null);
        
        // Fetch available plans for the plan selection UI
        fetchAvailablePlans();
        
        console.log('✅ Subscription permanently deleted. Plan selection UI will be shown.');
        
        // Also ensure we reload fresh data from server
        if (user?.emailAddresses?.[0]?.emailAddress) {
          const userEmail = user.emailAddresses[0].emailAddress;
          setTimeout(() => {
            fetchSubscriptionByEmail(userEmail);
          }, 500);
        }
      } else {
        console.log('🔄 Cancelling subscription:', subscriptionData.subscriptionId);
        result = await cancelSubscription(subscriptionData.subscriptionId);
        console.log('✅ Subscription cancelled successfully:', result);
        
        // For cancellation, we keep the subscription data but update status
        // User retains access until end of billing period
        setSubscriptionData((prev: any) => ({
          ...prev,
          status: 'cancelled'
        }));
        
        console.log('✅ Subscription cancelled successfully. Access retained until end of billing period.');
        
        // Show success message for cancellation
        setSubscriptionUpdateError('');
      }
      
      // Close modals and reset state
      setShowCancelConfirmation(false);
      setDeleteType('cancel'); // Reset to default
      
      // Auto-reload subscription data to reflect changes
      if (user?.emailAddresses?.[0]?.emailAddress) {
        const userEmail = user.emailAddresses[0].emailAddress;
        console.log('🔄 Auto-reloading subscription data after operation...');
        setTimeout(() => {
          fetchSubscriptionByEmail(userEmail);
        }, 500); // Small delay to ensure backend has processed the change
      }
      
    } catch (error: any) {
      console.error('❌ Error processing subscription:', error);
      const action = deleteType === 'delete' ? 'delete' : 'cancel';
      setSubscriptionUpdateError(`Failed to ${action} subscription: ` + (error.message || 'Please try again.'));
    } finally {
      setIsCancelling(false);
    }
  };

  // Create userData object from Firebase data and Clerk data
  const userData = {
    email: user?.emailAddresses?.[0]?.emailAddress || 'N/A',
    fullName: userProfileData?.fullName || user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'N/A',
    phone: userProfileData?.phoneNumber || localStorage.getItem('userPhone') || 'N/A',
    verified: true, // If user reached dashboard, they're verified
    lastLogin: new Date().toLocaleString(),
    createdAt: user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) : 'N/A'
  };

  // Debug logging
  console.log('🔍 Dashboard userData:', {
    userProfileData: userProfileData,
    clerkUser: user,
    finalUserData: userData,
    isLoadingProfile: isLoadingProfile,
    clerkEmail: user?.emailAddresses?.[0]?.emailAddress,
    fullNameFromProfile: userProfileData?.fullName,
    userIdFromProfile: userProfileData?.userId // Debug userId issues
  });

  const handleLogout = async () => {
    try {
      // Clear any legacy tokens and user data from localStorage
      TokenManager.clearTokens();
      localStorage.removeItem('userPhone');
      
      // Sign out from Clerk
      await signOut();
      
      // Redirect to landing page
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Even if Clerk signOut fails, clear local tokens
      TokenManager.clearTokens();
      localStorage.removeItem('userPhone');
      
      // Fallback to redirect
      window.location.href = '/';
    }
  };

  // Payment Methods Functions - Now get from user data
  const fetchPaymentMethods = async () => {
    if (!user?.emailAddresses?.[0]?.emailAddress) {
      console.log('No user email available for fetching payment methods');
      return;
    }

    setIsLoadingPaymentMethods(true);
    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      console.log('🔄 Fetching user data for payment methods:', userEmail);

      // Use the same email-based API as profile fetching
      const response = await fetch(`${API_BASE}/user/user-profile?email=${encodeURIComponent(userEmail)}`);
      const result = await response.json();
      
      console.log('🔍 User data API response:', result);

      if (result.success && result.users && result.users.length > 0) {
        // Find the user with the most complete data (same logic as profile fetching)
        let selectedUser = result.users[0];
        for (const userData of result.users) {
          if (userData.fullName !== 'Unknown User') {
            selectedUser = userData;
            break;
          }
        }

        console.log('🔍 Selected user for payment methods:', selectedUser);

        if (selectedUser?.cards && Array.isArray(selectedUser.cards)) {
          // Use the new cards array structure
          const cardsArray = selectedUser.cards.map((card: any, index: number) => {
            return {
              id: `card_${index}`, // Generate a simple ID based on index
              paymentMethodId: card.stripePaymentMethodId,
              email: userEmail,
              phoneNumber: selectedUser.phoneNumber,
              cardDetails: {
                cardNumber: null, // Never store full card number
                cvv: null, // Never store CVV
                last4: card.last4,
                brand: card.brand,
                expMonth: card.expMonth,
                expYear: card.expYear,
                cardholderName: card.cardholderName || selectedUser.fullName || ''
              },
              stripeCustomerId: card.stripeCustomerId,
              stripePaymentMethodId: card.stripePaymentMethodId,
              isActive: card.isActive,
              cardName: card.cardName, // "primaryCard", "secondaryCard", etc.
              isDefault: card.cardName === "primaryCard", // Primary card is default
              createdAt: new Date().toISOString() // Use current time as fallback
            };
          }).filter((card: any) => card); // Filter out any null/undefined cards

          setPaymentMethods(cardsArray);
          console.log('✅ Cards loaded from user data:', cardsArray.length);
          console.log('✅ Cards data:', cardsArray);
        } else if (selectedUser?.paymentMethods) {
          // Fallback to old payment methods structure for backward compatibility
          console.log('⚠️ Using fallback to old paymentMethods structure');
          const paymentMethodsArray = Object.keys(selectedUser.paymentMethods).map(key => {
            const method = selectedUser.paymentMethods[key];
            return {
              id: key,
              paymentMethodId: method.paymentMethodId,
              email: method.email,
              phoneNumber: method.phoneNumber,
              cardDetails: {
                cardNumber: method.cardDetails?.cardNumber || null,
                cvv: method.cardDetails?.cvv || null,
                last4: method.cardDetails?.last4,
                brand: method.cardDetails?.brand,
                expMonth: method.cardDetails?.expMonth,
                expYear: method.cardDetails?.expYear,
                cardholderName: method.cardDetails?.cardholderName || ''
              },
              isDefault: method.isDefault,
              createdAt: method.createdAt?.toDate?.()?.toISOString() || method.createdAt
            };
          }).filter(method => method);

          setPaymentMethods(paymentMethodsArray);
          console.log('✅ Payment methods loaded from legacy structure:', paymentMethodsArray.length);
        } else {
          console.log('No payment methods or cards found in selected user data');
          setPaymentMethods([]);
        }
      } else {
        console.log('No users found for email:', userEmail);
        setPaymentMethods([]);
      }

    } catch (error) {
      console.error('❌ Error fetching user data for payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const toggleFieldVisibility = (fieldName: string) => {
    setHiddenFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Set payment method as default - Now handled through user update
  const setDefaultPaymentMethod = async (paymentMethodId: string) => {
    if (!user?.emailAddresses?.[0]?.emailAddress) {
      console.error('No user email available');
      return;
    }

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      
      // Generate userId from email (same logic as backend)
      const generateUserIdFromEmail = (email: string) => {
        const cleanEmail = email.toLowerCase().trim();
        const username = cleanEmail.split('@')[0];
        const domain = cleanEmail.split('@')[1];
        const shortDomain = domain.split('.')[0];
        const timestamp = Date.now().toString().slice(-6);
        return `${username}_${shortDomain}_${timestamp}`;
      };
      
      const userId = generateUserIdFromEmail(userEmail);
      
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      console.log('⭐ Setting payment method as default:', paymentMethodId);

      // Get current user data
      const userResponse = await fetch(`${API_BASE}/users/${userId}`);
      const userResult = await userResponse.json();

      if (!userResult.success || !userResult.user?.paymentMethods) {
        console.error('❌ Failed to get user data');
        return;
      }

      // Update payment methods to set the selected one as default
      const paymentMethods = userResult.user.paymentMethods;
      Object.keys(paymentMethods).forEach(key => {
        if (paymentMethods[key]) {
          paymentMethods[key].isDefault = key === paymentMethodId;
          paymentMethods[key].updatedAt = new Date();
        }
      });

      // Update user with new payment methods
      const updateResponse = await fetch(`${API_BASE}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethods: paymentMethods
        })
      });

      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        console.log('✅ Payment method set as default successfully');
        // Refresh payment methods
        await fetchPaymentMethods();
      } else {
        console.error('❌ Failed to set payment method as default:', updateResult.message);
      }
    } catch (error) {
      console.error('❌ Error setting payment method as default:', error);
    }
  };

  // Remove payment method - Now handled through user update
  const removePaymentMethod = async (paymentMethodId: string) => {
    if (!user?.emailAddresses?.[0]?.emailAddress) {
      console.error('No user email available');
      return;
    }

    const isConfirmed = window.confirm('Are you sure you want to remove this payment method? This action cannot be undone.');
    if (!isConfirmed) {
      return;
    }

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      
      // Generate userId from email (same logic as backend)
      const generateUserIdFromEmail = (email: string) => {
        const cleanEmail = email.toLowerCase().trim();
        const username = cleanEmail.split('@')[0];
        const domain = cleanEmail.split('@')[1];
        const shortDomain = domain.split('.')[0];
        const timestamp = Date.now().toString().slice(-6);
        return `${username}_${shortDomain}_${timestamp}`;
      };
      
      const userId = generateUserIdFromEmail(userEmail);
      
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      console.log('🗑️ Removing payment method:', paymentMethodId);

      // Get current user data
      const userResponse = await fetch(`${API_BASE}/users/${userId}`);
      const userResult = await userResponse.json();

      if (!userResult.success || !userResult.user?.paymentMethods) {
        console.error('❌ Failed to get user data');
        return;
      }

      // Check if this is the only payment method
      const paymentMethods = userResult.user.paymentMethods;
      const activeMethods = Object.keys(paymentMethods).filter(key => 
        paymentMethods[key] && paymentMethods[key].isActive !== false
      );

      if (activeMethods.length === 1 && activeMethods[0] === paymentMethodId) {
        alert('Cannot remove the only payment method. Please add another payment method first.');
        return;
      }

      // Mark payment method as inactive (soft delete)
      paymentMethods[paymentMethodId].isActive = false;
      paymentMethods[paymentMethodId].updatedAt = new Date();

      // If this was the default, set another method as default
      if (paymentMethods[paymentMethodId].isDefault) {
        const remainingMethods = Object.keys(paymentMethods).filter(key => 
          key !== paymentMethodId && paymentMethods[key] && paymentMethods[key].isActive !== false
        );
        
        if (remainingMethods.length > 0) {
          paymentMethods[remainingMethods[0]].isDefault = true;
          paymentMethods[remainingMethods[0]].updatedAt = new Date();
        }
      }

      // Update user with modified payment methods
      const updateResponse = await fetch(`${API_BASE}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethods: paymentMethods
        })
      });

      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        console.log('✅ Payment method removed successfully');
        // Refresh payment methods to show updated list
        await fetchPaymentMethods();
      } else {
        console.error('❌ Failed to remove payment method:', updateResult.message);
      }

    } catch (error) {
      console.error('❌ Error removing payment method:', error);
    }
  };

  // Open update card modal
  const openUpdateCardModal = (paymentMethod: any) => {
    console.log('🔧 Opening update card modal for payment method:', paymentMethod);
    setSelectedPaymentMethod(paymentMethod);
    setUpdateCardData({
      cardholderName: paymentMethod.cardDetails.cardholderName || '',
      expiryMonth: paymentMethod.cardDetails.expMonth.toString() || '',
      expiryYear: paymentMethod.cardDetails.expYear.toString() || ''
    });
    setShowUpdateCardModal(true);
    console.log('🔧 Update card modal state set to true');
  };

  // Close update card modal
  const closeUpdateCardModal = () => {
    setShowUpdateCardModal(false);
    setSelectedPaymentMethod(null);
    setUpdateCardData({
      cardholderName: '',
      expiryMonth: '',
      expiryYear: ''
    });
  };

  // Update card details - Now handled through user update
  const updateCardDetails = async () => {
    if (!selectedPaymentMethod || !user?.emailAddresses?.[0]?.emailAddress) {
      console.error('No payment method selected or user email available');
      return;
    }

    console.log('🔧 Starting card update...');
    console.log('🔧 Selected payment method:', selectedPaymentMethod);
    console.log('🔧 Update card data:', updateCardData);

    setIsUpdatingCard(true);

    try {
      const userEmail = user.emailAddresses[0].emailAddress;
      
      const API_BASE = (() => {
        const prodUrl = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
        const localUrl = 'http://localhost:5001';
        
        if (process.env.REACT_APP_USE_PROD_API === 'true') {
          return prodUrl;
        }
        
        if (process.env.NODE_ENV === 'production') {
          return prodUrl;
        }
        
        return localUrl;
      })();

      console.log('📝 Updating card details for:', selectedPaymentMethod.paymentMethodId || selectedPaymentMethod.stripePaymentMethodId);

      // Get current user data using email-based API
      const userResponse = await fetch(`${API_BASE}/user/user-profile?email=${encodeURIComponent(userEmail)}`);
      const userResult = await userResponse.json();

      console.log('📝 User data response:', userResult);

      if (!userResult.success || !userResult.users || userResult.users.length === 0) {
        console.error('❌ Failed to get user data or no users found');
        return;
      }

      // Find the user with the most complete data (same logic as fetching)
      let selectedUser = userResult.users[0];
      for (const userData of userResult.users) {
        if (userData.fullName !== 'Unknown User') {
          selectedUser = userData;
          break;
        }
      }

      console.log('📝 Selected user for update:', selectedUser);

      // Update the card in the cards array
      let updatedCards = [];
      let updatedPaymentMethods = null;
      
      if (selectedUser.cards && Array.isArray(selectedUser.cards)) {
        // Use new cards array structure
        console.log('📝 Updating cards array, looking for:', selectedPaymentMethod.stripePaymentMethodId);
        updatedCards = selectedUser.cards.map((card: any) => {
          console.log('📝 Checking card:', card.stripePaymentMethodId);
          // Find the card to update by stripePaymentMethodId
          if (card.stripePaymentMethodId === selectedPaymentMethod.stripePaymentMethodId) {
            console.log('📝 Found matching card, updating...');
            return {
              ...card,
              expMonth: parseInt(updateCardData.expiryMonth),
              expYear: parseInt(updateCardData.expiryYear),
              cardholderName: updateCardData.cardholderName
            };
          }
          return card;
        });
        console.log('📝 Updated cards array:', updatedCards);
      } else if (selectedUser.paymentMethods) {
        // Fallback to old payment methods structure
        console.log('📝 Using fallback payment methods structure');
        updatedPaymentMethods = { ...selectedUser.paymentMethods };
        const paymentMethodId = selectedPaymentMethod.paymentMethodId;
        
        if (updatedPaymentMethods[paymentMethodId]) {
          updatedPaymentMethods[paymentMethodId].cardDetails = {
            ...updatedPaymentMethods[paymentMethodId].cardDetails, // Keep existing card details
            cardholderName: updateCardData.cardholderName,
            expMonth: parseInt(updateCardData.expiryMonth),
            expYear: parseInt(updateCardData.expiryYear)
          };
          updatedPaymentMethods[paymentMethodId].updatedAt = new Date();
        }
      }

      // Prepare update payload
      const updatePayload: any = {
        email: userEmail
      };
      
      if (updatedCards.length > 0) {
        // Send both cards array and convert to paymentMethods format for compatibility
        updatePayload.cards = updatedCards;
        
        // Also convert to legacy paymentMethods format for backward compatibility
        const legacyPaymentMethods: any = {};
        updatedCards.forEach((card: any, index: number) => {
          const methodId = card.stripePaymentMethodId || `method_${index}`;
          legacyPaymentMethods[methodId] = {
            paymentMethodId: card.stripePaymentMethodId,
            stripeCustomerId: card.stripeCustomerId,
            stripePaymentMethodId: card.stripePaymentMethodId,
            email: userEmail,
            phoneNumber: selectedUser.phoneNumber,
            cardDetails: {
              brand: card.brand,
              last4: card.last4,
              expMonth: card.expMonth,
              expYear: card.expYear,
              cardholderName: card.cardholderName
            },
            isDefault: card.cardName === "primaryCard",
            createdAt: new Date(),
            updatedAt: new Date()
          };
        });
        updatePayload.paymentMethods = legacyPaymentMethods;
        
        console.log('📝 Adding both cards and legacy payment methods to update payload');
      } else if (updatedPaymentMethods) {
        updatePayload.paymentMethods = updatedPaymentMethods;
        console.log('📝 Adding payment methods to update payload');
      } else {
        console.error('❌ No payment data to update');
        return;
      }

      console.log('📝 Final update payload:', updatePayload);

      // Update user with modified card data
      const updateResponse = await fetch(`${API_BASE}/user/user-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload)
      });

      console.log('📝 Update response status:', updateResponse.status, updateResponse.statusText);
      
      const updateResult = await updateResponse.json();
      console.log('📝 Update result:', updateResult);

      if (updateResult.success) {
        console.log('✅ Card details updated successfully');
        // Close modal and refresh payment methods
        closeUpdateCardModal();
        await fetchPaymentMethods();
      } else {
        console.error('❌ Failed to update card details:', updateResult.message);
        alert(`Failed to update card details: ${updateResult.message || 'Unknown error'}`);
      }

    } catch (error) {
      console.error('❌ Error updating card details:', error);
      alert('An error occurred while updating card details. Please try again.');
    } finally {
      setIsUpdatingCard(false);
    }
  };

  // Load payment methods when subscription section is accessed
  useEffect(() => {
    if (activeSection === 'subscription' && isLoaded && user) {
      fetchPaymentMethods();
    }
  }, [activeSection, isLoaded, user]);

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
  ];

  const renderOverview = () => (
    <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100 mb-6">
  
  <div className="flex justify-between mb-4">
      <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
        Welcome back, {userData.fullName}!
      </h2>
                  <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-black-300 text-black-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 font-medium transform hover:scale-105 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Data
            </button>
            {/* {process.env.NODE_ENV === 'development' && <ErrorButton />}  */}
      </div>
      <p className="text-gray-600 mb-8 text-lg leading-relaxed">
        Manage your account settings and subscription from this dashboard.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 transition-all duration-300 hover:shadow-md hover:scale-[1.02] group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 600 }}>
            Account Status
          </h3>
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          </div>
          <p className="text-black font-medium text-xl" style={{ fontWeight: 600 }}>
            Active
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Your account is in good standing
          </p>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 transition-all duration-300 hover:shadow-md hover:scale-[1.02] group">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-black group-hover:scale-110 transition-transform duration-300" style={{ fontWeight: 600 }}>
            Current Plan
          </h3>
            <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
              <div className="w-3 h-3 border border-black rounded-sm"></div>
            </div>
          </div>
          <p className="text-black font-medium text-xl" style={{ fontWeight: 600 }}>
            {isLoadingSubscription ? 'Loading...' : subscriptionData ? 
              (subscriptionData.status === 'trial' ? 'Free Trial Plan' :
               planData?.name || `${subscriptionData.planId?.charAt(0).toUpperCase()}${subscriptionData.planId?.slice(1)}` || 'Active Plan') :
              'No Plan'
            }
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {isLoadingSubscription ? 'Loading...' : subscriptionData ? 
              (subscriptionData.status === 'trial' ? 
                `Trial ends: ${formatShortDate(subscriptionData.trialEndDate || subscriptionData.endDate)}` :
                subscriptionData.billingCycle === 'yearly' 
                ? `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/year • Next billing: ${formatShortDate(subscriptionData.endDate)}`
                : `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/month • Next billing: ${formatShortDate(subscriptionData.endDate)}`
              ) : 
              'No active subscription'
            }
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-black mb-4" style={{ fontWeight: 600 }}>Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setActiveSection('account')}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-300 font-medium transform hover:scale-105 hover:shadow-md"
          >
            Update Account
          </button>
          <button
            onClick={() => setActiveSection('subscription')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:border-black hover:bg-gray-50 transition-all duration-300 font-medium transform hover:scale-105"
          >
            Manage Subscription
          </button>
          {/* Only show in development */}
          {/* {process.env.NODE_ENV === 'development' && <ErrorButton />} */}
        </div>
      </div>
    </div>
  );

  const renderAccountSection = () => (
    <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100 mb-6">
      <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
        Account Settings
      </h2>
      <p className="text-gray-600 mb-8 text-lg">
        Manage your personal information and account preferences.
      </p>
      
      <div className="max-w-2xl space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
          </div>
          
          {isEditingName ? (
            <div className="space-y-3">
              <input
                type="text"
                value={editingFullName}
                onChange={(e) => {
                  setEditingFullName(e.target.value);
                  if (updateError) setUpdateError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all duration-200"
                placeholder="Enter your full name"
                disabled={isUpdating}
                autoFocus
              />
              
              {updateError && (
                <p className="text-sm text-red-600">{updateError}</p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleSaveFullName}
                  disabled={isUpdating || !editingFullName.trim()}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 font-medium"
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancelEditName}
                  disabled={isUpdating}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <input
              type="text"
              value={userData.fullName}
              onClick={handleStartEditName}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 cursor-text hover:border-gray-400 focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all duration-200"
              placeholder="Click to edit your full name"
              readOnly
            />
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={userData.email}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <button
              onClick={handleOpenPasswordModal}
              className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200"
            >
              <Edit3 size={16} />
              Update
            </button>
          </div>
          <input
            type="password"
            value="••••••••••••"
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-500">Your password is securely managed by our authentication system</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <input
            type="text"
            value={userData.phone}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Member Since
          </label>
          <input
            type="text"
            value={userData.createdAt}
            readOnly
            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
          />
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded flex items-center justify-center mt-0.5">
              <div className="w-3 h-3 border border-black rounded-sm"></div>
            </div>
            <div>
              <h4 className="font-medium text-black mb-1">Account Security</h4>
              <p className="text-gray-600 text-sm">
                Your account is managed through Clerk's secure authentication system. 
                To update your password or security settings, please contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );



  // Simple Password Update Modal
  const renderPasswordModal = () => {
    if (!showPasswordModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 w-full max-w-md shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-black">Update Password</h3>
              <p className="text-sm text-gray-600">Change your password securely</p>
            </div>
            <button
              onClick={handleClosePasswordModal}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <X size={20} />
            </button>
          </div>

          {passwordError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{passwordError}</p>
            </div>
          )}

          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">{passwordSuccess}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setPasswordError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter your current password"
                disabled={isUpdatingPassword}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Enter your new password"
                disabled={isUpdatingPassword}
              />
              <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters long</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Confirm your new password"
                disabled={isUpdatingPassword}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClosePasswordModal}
              disabled={isUpdatingPassword}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handlePasswordUpdate}
              disabled={isUpdatingPassword || !currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingPassword ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Security Note:</strong> Updating your password will sign you out of all other devices and sessions for your security.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Separate Billing History Section
  const renderBillingHistorySection = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-xl font-bold text-black mb-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700 }}>
            Billing History
          </h4>
          <p className="text-gray-600 text-sm">
            Track all your payment transactions and subscription changes
          </p>
        </div>
        <div className="flex gap-2">
          {showBillingHistory && (
            <button
              onClick={() => fetchBillingHistory(user?.emailAddresses?.[0]?.emailAddress || '')}
              disabled={isLoadingBillingHistory}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
            >
              {isLoadingBillingHistory ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                  Refreshing...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </div>
              )}
            </button>
          )}
          <button
            onClick={() => setShowBillingHistory(!showBillingHistory)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
              showBillingHistory 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            {showBillingHistory ? 'Hide History' : 'View History'}
          </button>
        </div>
      </div>
      
      {showBillingHistory && (
        <div>
          {isLoadingBillingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
              <p className="text-gray-600">Loading billing history...</p>
            </div>
          ) : billingHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h5 className="text-lg font-semibold text-gray-900 mb-2">No Billing History Found</h5>
              <p className="text-gray-600 mb-1">
                We couldn't find any billing records for your account.
              </p>
              <p className="text-gray-500 text-sm mb-4">
                This could mean you haven't made any payments yet, or there might be an issue with the data.
              </p>
              <button
                onClick={() => fetchBillingHistory(user?.emailAddresses?.[0]?.emailAddress || '')}
                disabled={isLoadingBillingHistory}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium border border-blue-300 rounded-lg hover:bg-blue-50 transition-all duration-200 disabled:opacity-50"
              >
                {isLoadingBillingHistory ? 'Checking...' : 'Try Again'}
              </button>
              
              {/* Debug Information */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-left">
                <h6 className="text-sm font-medium text-gray-700 mb-2">Debug Information:</h6>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>User Email: {user?.emailAddresses?.[0]?.emailAddress || 'Not found'}</p>
                  <p>Billing History Count: {billingHistory.length}</p>
                  <p>Loading State: {isLoadingBillingHistory ? 'Yes' : 'No'}</p>
                  <p>Show History: {showBillingHistory ? 'Yes' : 'No'}</p>
                </div>
                
                {/* Test API Button */}
                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => {
                      const userEmail = user?.emailAddresses?.[0]?.emailAddress;
                      if (userEmail) {
                        console.log('🧪 Testing billing API manually...');
                        fetchBillingHistory(userEmail);
                      }
                    }}
                    className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200 transition-colors"
                  >
                    Test Billing API
                  </button>
                  
                  <button
                    onClick={async () => {
                      const userEmail = user?.emailAddresses?.[0]?.emailAddress;
                      if (userEmail) {
                        console.log('🔍 Testing direct API call...');
                        try {
                          const API_BASE = 'https://us-central1-silas-5fdd2.cloudfunctions.net/api';
                          const response = await fetch(`${API_BASE}/billing/history/${encodeURIComponent(userEmail)}`);
                          console.log('📡 Direct API Response:', {
                            status: response.status,
                            statusText: response.statusText,
                            ok: response.ok
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            console.log('📊 Direct API Data:', data);
                            alert(`API Response: ${JSON.stringify(data, null, 2)}`);
                          } else {
                            alert(`API Error: ${response.status} ${response.statusText}`);
                          }
                        } catch (error) {
                          const errorMessage = error instanceof Error ? error.message : String(error);
                          console.error('❌ Direct API test failed:', error);
                          alert(`API Test Failed: ${errorMessage}`);
                        }
                      }
                    }}
                    className="px-3 py-1.5 text-xs bg-green-100 text-green-700 border border-green-300 rounded hover:bg-green-200 transition-colors"
                  >
                    Test Direct API
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Total Transactions</p>
                      <p className="text-2xl font-bold text-blue-700">{billingHistory.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-900">Total Spent</p>
                      <p className="text-2xl font-bold text-green-700">
                        ${billingHistory.reduce((sum, billing) => sum + billing.amountCharged, 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v16a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-purple-900">Last Payment</p>
                      <p className="text-lg font-bold text-purple-700">
                        {billingHistory.length > 0 ? new Date(billingHistory[0].createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Records */}
              <div className="space-y-3">
                {billingHistory.map((billing, index) => (
                  <div key={billing.id} className="bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200 hover:shadow-md transition-all duration-200">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3">
                      <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                          <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                            <h5 className="text-lg md:text-xl font-bold text-gray-900">
                              ${billing.amountCharged.toFixed(2)}
                            </h5>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 w-fit">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Paid
                            </span>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 mb-2">
                            {new Date(billing.createdAt).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1 truncate">
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="truncate">{billing.email}</span>
                            </span>
                            <span className="flex items-center gap-1 truncate">
                              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span className="truncate">{billing.userId}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-left sm:text-right flex-shrink-0">
                        <div className="text-xs text-gray-500 mb-2">Transaction #{index + 1}</div>
                        <div className="space-y-1">
                          <div className="text-xs">
                            <span className="text-gray-500">Subscription:</span>
                            <p className="text-gray-900 font-mono text-xs truncate max-w-full sm:max-w-32" title={billing.subscriptionId}>
                              {billing.subscriptionId}
                            </p>
                          </div>
                          {billing.stripePaymentIntentId && (
                            <div className="text-xs">
                              <span className="text-gray-500">Payment ID:</span>
                              <p className="text-gray-900 font-mono text-xs truncate max-w-full sm:max-w-32" title={billing.stripePaymentIntentId}>
                                {billing.stripePaymentIntentId}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expandable Details */}
                    <div className="border-t border-gray-200 pt-3 md:pt-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 font-medium text-xs md:text-sm">Stripe Customer ID:</span>
                          <p className="text-gray-900 font-mono text-xs break-all bg-gray-100 p-2 rounded mt-1">
                            {billing.stripeCustomerId}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium text-xs md:text-sm">Transaction Date:</span>
                          <p className="text-gray-900 text-xs md:text-sm mt-1">
                            {new Date(billing.createdAt).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderSubscriptionSection = () => {
    if (isLoadingSubscription) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
            Subscription Management
          </h2>
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
            <p className="text-gray-600">Loading subscription data...</p>
          </div>
          </div>
          {renderBillingHistorySection()}
        </div>
      );
    }

    if (!subscriptionData) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
            Subscription Management
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Renew Subscription
          </p>
          
          {isLoadingPlans ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
              <p className="text-gray-600">Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Billing Cycle Toggle */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-100 p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'monthly'
                        ? 'bg-black text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    MONTHLY
                  </button>
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'yearly'
                        ? 'bg-black text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    YEARLY (20% Off)
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {['Starter', 'Standard', 'Pro'].map((planType) => {
                  // Ensure plan ID matches exactly what's in the database
                  const planId = `${planType.toLowerCase()}_${billingCycle}`;
                  console.log('🔍 Plan card ID:', planId);
                  
                  const planData = getPlanFromDB(planType, billingCycle);
                  const isSelected = selectedPlanId === planId;
                  const isPopular = planType === 'Standard';
                  
                  return (
                    <div
                      key={planId}
                      className={`relative border rounded-xl p-4 md:p-6 cursor-pointer transition-all duration-300 ${
                        isSelected 
                          ? 'border-black bg-black text-white transform scale-105 shadow-lg' 
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                      onClick={() => handlePlanSelection(planId)}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-black text-white px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                            POPULAR
                          </span>
                        </div>
                      )}

                      <div className="text-center">
                        <h4 className={`text-lg md:text-xl font-bold mb-2 ${isSelected ? 'text-white' : 'text-black'}`}>
                          {planType}
                        </h4>
                        <p className={`text-xs md:text-sm mb-3 md:mb-4 ${isSelected ? 'text-gray-200' : 'text-gray-600'} line-clamp-2`}>
                          {planData.description}
                        </p>
                        
                        <div className="mb-3 md:mb-4">
                          <span className={`text-2xl md:text-4xl font-bold ${isSelected ? 'text-white' : 'text-black'}`}>
                            ${billingCycle === 'yearly' ? planData.price * 12 : planData.price}
                          </span>
                        </div>

                        <div className={`inline-block px-2 md:px-4 py-1.5 md:py-2 rounded-lg mb-2 w-full ${
                              isSelected ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                            }`}>
                              <span className="text-xs md:text-sm font-medium break-words">
                              {planData.messageLimit.toLocaleString()} messages included
                              </span>
                            </div>
                                              
                          <div className={`inline-block px-2 md:px-4 py-1.5 md:py-2 rounded-lg mb-2 w-full ${
                              isSelected ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                            }`}>
                              <span className="text-xs md:text-sm font-medium">
                              ${planData.price} per month
                              </span>
                            </div>

                        </div>


                      <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                        {planData.features.map((feature: string, index: number) => (
                          <div key={index} className="flex items-start gap-2 md:gap-3">
                            <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isSelected ? 'bg-white' : 'bg-black'
                            }`}>
                              <svg className={`w-2.5 h-2.5 md:w-3 md:h-3 ${isSelected ? 'text-black' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className={`text-xs md:text-sm ${isSelected ? 'text-white' : 'text-gray-700'} leading-tight`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                                            <div className="mt-auto">
                        {isSelected ? (
                          <div className="w-full py-2 md:py-3 bg-white text-black rounded-lg font-medium text-center flex items-center justify-center gap-2 text-sm md:text-base">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Selected
                          </div>
                        ) : (
                          <div className="w-full py-2 md:py-3 border border-gray-300 text-gray-700 rounded-lg font-medium text-center hover:bg-gray-50 transition-all duration-200 text-sm md:text-base">
                            Select Plan
                          </div>
                        )}
                      </div>
                    </div>
                  );
              })}
              </div>
            </>
          )}
          
          {subscriptionUpdateError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{subscriptionUpdateError}</p>
            </div>
          )}
          
          {/* Show payment method requirement notice */}
          {!userProfileData?.stripeCustomerId && (!paymentMethods || paymentMethods.length === 0) && (
            <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Payment method required for subscription actions. Please add a payment method first.
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-4">
            {/* Renew Current Plan Button */}
            {subscriptionData && (
              <button
                onClick={handleRenewSubscription}
                disabled={isUpdatingSubscription || isLoadingPaymentMethods || (!userProfileData?.stripeCustomerId && (!paymentMethods || paymentMethods.length === 0))}
                className="w-full px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUpdatingSubscription ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Renewal...
                  </>
                ) : isLoadingPaymentMethods ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading Payment Info...
                  </>
                ) : (
                  'Renew Current Plan'
                )}
              </button>
            )}

            {/* Create New Subscription Button */}
            {selectedPlanId && !subscriptionData && (
              // Only show if selected plan matches current billing cycle
              (billingCycle === 'yearly' && selectedPlanId.includes('_yearly')) ||
              (billingCycle === 'monthly' && !selectedPlanId.includes('_yearly'))
            ) && (
              <button
                onClick={handleCreateSubscription}
                disabled={isUpdatingSubscription || isLoadingPaymentMethods || (!userProfileData?.stripeCustomerId && (!paymentMethods || paymentMethods.length === 0))}
                className="w-full px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUpdatingSubscription ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Renewing Subscription...
                  </>
                ) : isLoadingPaymentMethods ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading Payment Info...
                  </>
                ) : (
                  'Upgrade'
                )}
              </button>
            )}
          </div>
          </div>
          {renderBillingHistorySection()}
        </div>
      );
    }



    const getStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'bg-green-100 text-green-800 border-green-200';
        case 'trial':
          return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'cancelled':
          return 'bg-red-100 text-red-800 border-red-200';
        case 'inactive':
          return 'bg-red-100 text-red-800 border-red-200';
        case 'past_due':
          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    const capitalizeFirst = (str: string) => {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    };

    // Helper functions for trial subscriptions
    const isTrialSubscription = () => {
      return subscriptionData?.status === 'trial' && subscriptionData?.isFreeTrial;
    };

    const calculateTrialDaysRemaining = () => {
      if (!subscriptionData?.trialEndDate) return 0;
      
      try {
        // Parse the trial end date
      const trialEndDate = new Date(subscriptionData.trialEndDate);
        
        // Validate the date
        if (isNaN(trialEndDate.getTime())) {
          console.warn('Invalid trialEndDate:', subscriptionData.trialEndDate);
          return 0;
        }
        
        // Get current date and normalize to start of day for accurate comparison
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Normalize trial end date to start of day
        const endDate = new Date(trialEndDate.getFullYear(), trialEndDate.getMonth(), trialEndDate.getDate());
        
        // Calculate difference in milliseconds
        const timeDiff = endDate.getTime() - currentDate.getTime();
        
        // Convert to days (add 1 to include the end date as a full day)
        const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
        
        // Ensure we never return negative days
      return Math.max(0, daysRemaining);
      } catch (error) {
        console.error('Error calculating trial days remaining:', error);
        return 0;
      }
    };

    const formatTrialEndDate = () => {
      if (!subscriptionData?.trialEndDate) return '';
      return formatDate(subscriptionData.trialEndDate);
    };

  return (
      <div className="space-y-6">
        {/* Main Subscription Section */}
        <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl md:text-3xl font-bold text-black mb-4" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
          {isTrialSubscription() ? 'Free Trial Active' : 'Subscription Management'}
        </h2>
        <p className="text-gray-600 mb-8 text-lg">
          {isTrialSubscription() 
            ? 'Your 7-day free trial is currently active.' 
            : 'View and manage your current subscription plan.'
          }
        </p>
        
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 mb-8">
          {isTrialSubscription() ? (
            // Trial subscription display
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-black mb-2" style={{ fontWeight: 600 }}>
                    {planData?.name || capitalizeFirst(subscriptionData.planId)} Plan - Free Trial
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Trial ends {formatTrialEndDate()}
                  </p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col items-end gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscriptionData.status)}`}>
                    <div className="w-2 h-2 rounded-full mr-2 bg-blue-500"></div>
                    Free Trial
                  </span>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      {calculateTrialDaysRemaining()} days remaining
                    </p>
                  </div>
                </div>
              </div>
              


              {/* Selected Plan Details (Read-only) */}
                <div className="bg-white rounded-lg p-3 md:p-4 border border-gray-200 mb-4 md:mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Plan Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    <div>
                      <span className="text-xs font-medium text-gray-500">Plan Type:</span>
                      <p className="text-sm text-gray-900 font-medium break-words">{planData?.name || capitalizeFirst(subscriptionData.planId)}</p>
                </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Billing Cycle:</span>
                      <p className="text-sm text-gray-900 font-medium">{capitalizeFirst(subscriptionData.billingCycle || 'monthly')}</p>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-1">
                      <span className="text-xs font-medium text-gray-500">After Trial Price:</span>
                      <p className="text-sm text-gray-900 font-medium break-words">
                        {subscriptionData.billingCycle === 'yearly' 
                          ? `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/year`
                          : `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/month`
                        }
                      </p>
                    </div>
                </div>
              </div>

              {/* After trial pricing info */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">After Trial Ends</h4>
                <p className="text-gray-600">
                  {subscriptionData.billingCycle === 'yearly' 
                    ? `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/year • Billed yearly`
                    : `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/month • Billed monthly`
                  }
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  You can cancel anytime before {formatTrialEndDate()}
                </p>
              </div>

            </>
          ) : (
            // Regular subscription display
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-black mb-2" style={{ fontWeight: 600 }}>
                    {planData?.name || capitalizeFirst(subscriptionData.planId)} Plan
                  </h3>
                  <p className="text-gray-600">
                      {subscriptionData.billingCycle === 'yearly' 
                        ? `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/year • Billed yearly`
                        : `$${getDisplayAmount(subscriptionData.amount, subscriptionData.billingCycle, subscriptionData.planId)}/month • Billed monthly`
                      }
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Currency: {subscriptionData.currency?.toUpperCase()}
                  </p>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col items-end gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscriptionData.status)}`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${subscriptionData.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                    {capitalizeFirst(subscriptionData.status)}
                  </span>
                </div>
              </div>
          
                {/* Subscription Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Start Date</h4>
                <p className="text-gray-900 text-sm break-words">{formatDate(subscriptionData.startDate)}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Next Billing Date</h4>
                <p className="text-gray-900 text-sm break-words">{formatDate(subscriptionData.endDate)}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Auto Renewal</h4>
                <p className="text-gray-900 text-sm">{subscriptionData.autoRenew ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Subscription ID</h4>
                <p className="text-gray-900 text-xs md:text-sm font-mono break-all">{subscriptionData.subscriptionId}</p>
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Payment Retry Count</h4>
                <p className={`text-sm font-medium ${
                  (subscriptionData.retryPaymentCount || 0) > 0 
                    ? 'text-orange-600' 
                    : 'text-green-600'
                }`}>
                  {subscriptionData.retryPaymentCount || 0}
                  {(subscriptionData.retryPaymentCount || 0) > 0 && (
                    <span className="text-xs text-gray-500 ml-1">failed attempts</span>
                  )}
                </p>
              </div>
            </div>
          
                {/* Plan Features */}
          {planData?.features && (
            <div className="space-y-3 mb-6">
              <h4 className="text-sm font-medium text-gray-700">Plan Features</h4>
              {planData.features.map((feature: string, index: number) => (
                <div key={index} className="flex items-center gap-3 text-gray-700">
                  <div className="w-5 h-5 bg-black rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          )}
          
                {/* Subscription Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={handleOpenUpgradePlanModal}
                  className="px-4 md:px-6 py-2 md:py-3 border border-black text-black rounded-lg hover:bg-black hover:text-white transition-all duration-300 font-medium transform hover:scale-105 text-sm md:text-base"
                >
                  Upgrade Plan
                </button>
                <button 
                  onClick={() => {
                    setDeleteType('cancel');
                    setShowCancelConfirmation(true);
                  }}
                  className="px-4 md:px-6 py-2 md:py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-all duration-300 font-medium transform hover:scale-105 text-sm md:text-base"
                >
                  Cancel Subscription
                </button>
                </div>
              </>
          )}
        </div>
        
        {subscriptionUpdateError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{subscriptionUpdateError}</p>
          </div>
        )}
        
        {/* Payment Methods Section */}
          <div>
          <h4 className="text-lg font-semibold text-black mb-4" style={{ fontWeight: 600 }}>
            Payment Methods
          </h4>
          <p className="text-gray-600 mb-6 text-sm">
            {isTrialSubscription() 
              ? "Update your payment method for when your trial ends."
              : "Manage your payment methods for auto-renewal and future purchases."
            }
          </p>

          {isLoadingPaymentMethods ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
              <p className="text-gray-600">Loading payment methods...</p>
            </div>
          ) : paymentMethods.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard size={20} className="text-gray-400" />
              </div>
              <h5 className="text-sm font-semibold text-gray-900 mb-1">No Payment Methods</h5>
              <p className="text-gray-500 text-xs">You haven't added any payment methods yet. Add one during your next purchase for easier checkout.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentMethods.map((method, index) => (
                <div key={method.id} className="border border-gray-200 rounded-lg p-3 md:p-4 hover:shadow-sm transition-all duration-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-6 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-gray-600 uppercase">
                          {method.cardDetails.brand}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <p className="font-semibold text-gray-900 text-sm">
                            •••• •••• •••• {method.cardDetails.last4}
                          </p>
                          {method.cardName && (
                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full w-fit">
                              {method.cardName === "primaryCard" ? "Primary" : method.cardName}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Expires {method.cardDetails.expMonth}/{method.cardDetails.expYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {method.isDefault && (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap">
                          Default
                        </span>
                      )}
                      {method.isActive && (
                        <span className="bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap">
                          Active
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-3">
                    {/* Cardholder Name Field */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">Cardholder Name:</span>
                      <div className="flex items-center gap-2">
                        {hiddenFields.cardholderName ? (
                          <span className="text-xs text-gray-500">••••••••</span>
                        ) : (
                          <span className="text-xs text-gray-900">{method.cardDetails.cardholderName || 'Not provided'}</span>
                        )}
                        <button
                          onClick={() => toggleFieldVisibility('cardholderName')}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 py-0.5 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                        >
                          {hiddenFields.cardholderName ? 'Show' : 'Hide'}
                        </button>
                      </div>
                    </div>

                    {/* Stripe Payment Method ID */}
                    {method.stripePaymentMethodId && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">Payment Method ID:</span>
                        <div className="flex items-center gap-2">
                          {hiddenFields.paymentMethodId ? (
                            <span className="text-xs text-gray-500">pm_••••••••••••</span>
                          ) : (
                            <span className="text-xs text-gray-900 font-mono">{method.stripePaymentMethodId}</span>
                          )}
                          <button
                            onClick={() => toggleFieldVisibility('paymentMethodId')}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 py-0.5 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                          >
                            {hiddenFields.paymentMethodId ? 'Show' : 'Hide'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Added Date */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">Added:</span>
                      <span className="text-xs text-gray-500">
                        {new Date(method.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    {!method.isDefault && (
                      <button 
                        onClick={() => setDefaultPaymentMethod(method.stripePaymentMethodId || method.paymentMethodId)}
                        className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-all duration-200 text-xs font-medium whitespace-nowrap"
                      >
                        Set as Default
                      </button>
                    )}
                    <button 
                      onClick={() => openUpdateCardModal(method)}
                      className="px-3 py-1.5 border border-blue-300 text-blue-600 rounded-md hover:bg-blue-50 transition-all duration-200 text-xs font-medium whitespace-nowrap"
                    >
                      Update Card
                    </button>
                    <button 
                      onClick={() => removePaymentMethod(method.stripePaymentMethodId || method.paymentMethodId)}
                      className="px-3 py-1.5 border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-all duration-200 text-xs font-medium whitespace-nowrap"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-2.5 h-2.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h5 className="text-xs font-medium text-blue-900 mb-1">Auto-Renewal Information</h5>
                <p className="text-xs text-blue-700">
                  Your default payment method will be used for automatic subscription renewals. 
                  You can change your default method at any time.
                </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Billing History Section */}
        {renderBillingHistorySection()}
      </div>
    );
  };



  // Upgrade Plan Modal
  const renderUpgradePlanModal = () => {
    if (!showUpgradePlanModal) return null;

    const currentPlanId = subscriptionData?.planId;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-6 w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-black" style={{ fontFamily: "'Canela', serif", fontWeight: 700 }}>
              Choose Your Plan
            </h3>
            <button
              onClick={handleCloseUpgradePlanModal}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <X size={24} />
            </button>
          </div>

          {subscriptionUpdateError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{subscriptionUpdateError}</p>
            </div>
          )}

          {isLoadingPlans ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-3"></div>
              <p className="text-gray-600">Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Billing Cycle Toggle */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-100 p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'monthly'
                        ? 'bg-black text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    MONTHLY
                  </button>
                  <button
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'yearly'
                        ? 'bg-black text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    YEARLY (20% Off)
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                {['Starter', 'Standard', 'Pro'].map((planType) => {
                  const planData = getPlanFromDB(planType, billingCycle);
                  const planId = billingCycle === 'yearly' ? `${planType.toLowerCase()}_yearly` : `${planType.toLowerCase()}_monthly`;
                  const isCurrentPlan = planId === currentPlanId;
                  const isSelected = selectedPlanId === planId;
                  const isPopular = planType === 'Standard';
                
                  return (
                    <div
                      key={planId}
                      className={`relative border rounded-xl p-4 md:p-6 cursor-pointer transition-all duration-300 ${
                        isSelected 
                          ? 'border-black bg-black text-white transform scale-105 shadow-lg' 
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                      onClick={() => setSelectedPlanId(planId)}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-black text-white px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                            POPULAR
                          </span>
                        </div>
                      )}
                      
                      {isCurrentPlan && (
                        <div className="absolute -top-3 right-2 md:right-4">
                          <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            isSelected ? 'bg-white text-black' : 'bg-green-100 text-green-800'
                          }`}>
                            Current Plan
                          </span>
                        </div>
                      )}

                      <div className="text-center">
                        <h4 className={`text-lg md:text-xl font-bold mb-2 ${isSelected ? 'text-white' : 'text-black'}`}>
                          {planType}
                        </h4>
                        <p className={`text-xs md:text-sm mb-3 md:mb-4 ${isSelected ? 'text-gray-200' : 'text-gray-600'} line-clamp-2`}>
                          {planData.description}
                        </p>
                        
                        <div className="mb-3 md:mb-4">
                          <span className={`text-2xl md:text-4xl font-bold ${isSelected ? 'text-white' : 'text-black'}`}>
                            ${billingCycle === 'yearly' ? planData.price * 12 : planData.price}
                          </span>
                          <span className={`text-sm md:text-lg ${isSelected ? 'text-gray-200' : 'text-gray-600'}`}>
                            /{billingCycle === 'yearly' ? 'year' : 'month'}
                          </span> 
                        </div>
                        
                        <div className={`inline-block px-2 md:px-4 py-1.5 md:py-2 rounded-lg mb-2 w-full ${
                              isSelected ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                            }`}>
                              <span className="text-xs md:text-sm font-medium break-words">
                              {planData.messageLimit.toLocaleString()} messages included
                              </span>
                            </div>
                                              
                          <div className={`inline-block px-2 md:px-4 py-1.5 md:py-2 rounded-lg mb-2 w-full ${
                              isSelected ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                            }`}>
                              <span className="text-xs md:text-sm font-medium">
                              ${planData.price} per month
                              </span>
                            </div>
                      </div>

                      <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                        {planData.features.map((feature: string, index: number) => (
                          <div key={index} className="flex items-start gap-2 md:gap-3">
                            <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isSelected ? 'bg-white' : 'bg-black'
                            }`}>
                              <svg className={`w-2.5 h-2.5 md:w-3 md:h-3 ${isSelected ? 'text-black' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className={`text-xs md:text-sm ${isSelected ? 'text-white' : 'text-gray-700'} leading-tight`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-auto">
                        {isSelected ? (
                          <div className="w-full py-2 md:py-3 bg-white text-black rounded-lg font-medium text-center flex items-center justify-center gap-2 text-sm md:text-base">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Selected
                          </div>
                        ) : (
                          <div className="w-full py-2 md:py-3 border border-gray-300 text-gray-700 rounded-lg font-medium text-center hover:bg-gray-50 transition-all duration-200 text-sm md:text-base">
                            Select Plan
                          </div>
                        )}
                      </div>
                    </div>
                  );
              })}
              </div>
            </>
          )}

          <div className="flex gap-3 justify-end border-t pt-6">
            <button
              onClick={handleCloseUpgradePlanModal}
              disabled={isUpdatingSubscription}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSubscriptionPlan}
              disabled={isUpdatingSubscription || !selectedPlanId || selectedPlanId === currentPlanId}
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUpdatingSubscription ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update Plan'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Cancel Subscription Confirmation Modal
  const renderCancelConfirmationModal = () => {
    if (!showCancelConfirmation) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
        style={{ zIndex: 99999 }}
      >
        <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-red-900">
              {deleteType === 'delete' ? 'Delete Subscription' : 'Cancel Subscription'}
            </h3>
            <button
              onClick={() => {
                setShowCancelConfirmation(false);
                setDeleteType('cancel'); // Reset to default
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              disabled={isCancelling}
            >
              <X size={20} />
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">Are you sure?</h4>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className={`border rounded-lg p-4 mb-4 ${deleteType === 'delete' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <h5 className={`font-medium mb-2 ${deleteType === 'delete' ? 'text-red-800' : 'text-yellow-800'}`}>
                {deleteType === 'delete' ? 'What happens when you delete:' : 'What happens when you cancel:'}
              </h5>
              <ul className={`text-sm space-y-1 ${deleteType === 'delete' ? 'text-red-700' : 'text-yellow-700'}`}>
                {deleteType === 'delete' ? (
                  <>
                    <li>• All subscription data will be permanently removed</li>
                    <li>• Your access will end immediately</li>
                    <li>• This action cannot be undone</li>
                    <li>• You'll need to create a new subscription to re-subscribe</li>
                  </>
                ) : (
                  <>
                    <li>• You'll retain access until the end of your current billing period</li>
                    <li>• Your subscription will not renew automatically</li>
                    <li>• You can resubscribe at any time</li>
                    <li>• Your account data will be preserved</li>
                  </>
                )}
              </ul>
            </div>

            <p className="text-gray-600 text-sm">
              You are about to {deleteType === 'delete' ? 'permanently delete' : 'cancel'} your <strong>{planData?.name || (subscriptionData?.planId || 'Your')} Plan</strong> subscription.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowCancelConfirmation(false);
                setDeleteType('cancel'); // Reset to default
              }}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCancelling}
            >
              {isCancelling ? 'Processing...' : 'Keep Subscription'}
            </button>
            <button
              onClick={handleCancelSubscription}
              className={`flex-1 px-4 py-3 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                deleteType === 'delete' 
                  ? 'bg-red-700 hover:bg-red-800' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {deleteType === 'delete' ? 'Deleting...' : 'Cancelling...'}
                </>
              ) : (
                deleteType === 'delete' ? 'Yes, Delete Forever' : 'Yes, Cancel'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };



  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'account':
        return renderAccountSection();
      case 'subscription':
        return renderSubscriptionSection();
      default:
        return renderOverview();
    }
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 shadow-lg transform transition-all duration-300 lg:translate-x-0 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } ${
        isSidebarCollapsed ? 'lg:w-20' : 'lg:w-80'
      } w-80`}>
        {/* Sidebar Header */}
        <div className={`p-6 border-b border-gray-100 flex items-center ${isSidebarCollapsed ? 'lg:justify-center' : 'justify-between'}`}>
          {!isSidebarCollapsed && (
            <img 
              src="/images/noBgColor.png" 
              alt="Silas Logo" 
              className="h-8 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          )}
          {/* Close/Collapse button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (window.innerWidth >= 1024) {
                  setIsSidebarCollapsed(!isSidebarCollapsed);
                } else {
                  setIsSidebarOpen(false);
                }
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-300"
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isSidebarCollapsed ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* User Info */}
        {!isSidebarCollapsed && (
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white font-semibold">
                {userData.fullName?.[0]?.toUpperCase() || user?.firstName?.[0]?.toUpperCase() || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="font-medium text-black">{userData.fullName}</p>
                <p className="text-sm text-gray-500">{userData.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed User Avatar */}
        {isSidebarCollapsed && (
          <div className="p-4 border-b border-gray-100 flex justify-center">
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {userData.fullName?.[0]?.toUpperCase() || user?.firstName?.[0]?.toUpperCase() || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || 'U'}
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="flex-1 p-4">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-4 py-3 rounded-lg transition-all duration-300 mb-2 group ${
                  isActive 
                    ? 'bg-black text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                }`}
                title={isSidebarCollapsed ? item.label : undefined}
              >
                <Icon size={20} className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                {!isSidebarCollapsed && <span className="font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 font-medium group`}
            title={isSidebarCollapsed ? "Logout" : undefined}
          >
            <LogOut size={20} className="group-hover:scale-110 transition-transform duration-300" />
            {!isSidebarCollapsed && "Logout"}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-80'}`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
            <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 text-gray-600 hover:text-black hover:bg-gray-50 rounded-lg transition-all duration-300"
            >
              <Menu size={24} />
            </button>
            
              <h1 className="text-2xl lg:text-3xl font-bold text-black" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
              {sidebarItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
            </h1>
          </div>

            {/* User Avatar for larger screens */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white font-semibold">
                {userData.fullName?.[0]?.toUpperCase() || user?.firstName?.[0]?.toUpperCase() || user?.emailAddresses?.[0]?.emailAddress?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="text-right">
                <p className="font-medium text-black">{userData.fullName}</p>
              </div>
            </div>
        </div>
        </header>

        {/* Content */}
        <main className="p-4 lg:p-8">
          <div className="max-w-5xl mx-auto">
          {renderContent()}
        </div>
        </main>
      </div>
      
      {/* Simple Password Modal */}
      {renderPasswordModal()}
      
      {/* Subscription Edit Modal */}
      
      
      {/* Upgrade Plan Modal */}
      {renderUpgradePlanModal()}
        
        {/* Update Card Modal */}
        {showUpdateCardModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-black" style={{ fontFamily: "'Canela', serif", fontWeight: 700 }}>
                  Update Card Details
                </h3>
                <button
                  onClick={closeUpdateCardModal}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <X size={20} />
                </button>
              </div>

              {selectedPaymentMethod && (
                <div className="mb-6">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="w-12 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                      <span className="text-xs font-bold text-gray-600 uppercase">
                        {selectedPaymentMethod.cardDetails.brand}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        •••• •••• •••• {selectedPaymentMethod.cardDetails.last4}
                      </p>
                      <p className="text-sm text-gray-500">
                        Current card
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    value={updateCardData.cardholderName}
                    onChange={(e) => setUpdateCardData(prev => ({ ...prev, cardholderName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    placeholder="Enter cardholder name"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Month
                    </label>
                    <select
                      value={updateCardData.expiryMonth}
                      onChange={(e) => setUpdateCardData(prev => ({ ...prev, expiryMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="">Month</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <option key={month} value={month}>
                          {month.toString().padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Year
                    </label>
                    <select
                      value={updateCardData.expiryYear}
                      onChange={(e) => setUpdateCardData(prev => ({ ...prev, expiryYear: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="">Year</option>
                      {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={closeUpdateCardModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={updateCardDetails}
                  disabled={isUpdatingCard || !updateCardData.cardholderName || !updateCardData.expiryMonth || !updateCardData.expiryYear}
                  className="flex-1 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCard ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update Card'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      
      {/* Cancel Subscription Confirmation Modal */}
      {renderCancelConfirmationModal()}

      {/* Cancel Trial Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md mx-auto my-auto max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Cancel Free Trial</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your free trial? You will lose access to all premium features immediately.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you're cancelling..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                disabled={isCancellingTrial}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
              >
                Keep Trial
              </button>
              <button
                onClick={handleCancelTrial}
                disabled={isCancellingTrial}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCancellingTrial ? 'Cancelling...' : 'Cancel Trial'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
